<template>
  <div class="order-detail-tab order-detail-tab--material">
    <template v-if="!orderId">
      <a-empty description="暂无订单数据" />
    </template>
    <template v-else-if="loading">
      <a-spin tip="加载中..." />
    </template>
    <template v-else-if="!hasData">
      <a-empty description="暂无辅材/打赏记录" />
    </template>
    <div v-else class="tab-content">
      <div
        v-for="group in groupedByMaster"
        :key="group.master.id"
        class="reward-master-block"
      >
        <div
          class="reward-master-header"
          @click="toggleExpand(group.master.id)"
        >
          <a-avatar
            :src="group.master.avatar"
            :size="40"
          >
            {{ (group.master.nickname && group.master.nickname.charAt(0)) || (group.master.name && group.master.name.charAt(0)) || '师' }}
          </a-avatar>
          <div class="reward-master-info">
            <span class="reward-master-name">{{ group.master.nickname || group.master.name || '师傅' }}</span>
            <span v-if="group.master.mobile" class="reward-master-mobile">{{ group.master.mobile }}</span>
          </div>
          <span class="reward-master-expand">
            <span class="reward-master-expand-text">{{ isExpanded(group.master.id) ? '收起' : '展开' }}</span>
            <a-icon :type="isExpanded(group.master.id) ? 'down' : 'right'" />
            <span class="reward-master-count">共 {{ group.records.length }} 条</span>
          </span>
        </div>
        <div v-show="isExpanded(group.master.id)" class="reward-master-body">
          <template v-if="group.records.length > 0">
            <div
              v-for="dateGroup in groupRecordsByDate(group.records)"
              :key="dateGroup.date"
              class="timeline-date-group"
            >
              <div class="timeline-line" />
              <div class="timeline-date-header">
                <span class="timeline-date-dot" />
                <span class="timeline-date-text">{{ dateGroup.date }}</span>
              </div>
              <div
                v-for="(item, rIdx) in dateGroup.records"
                :key="item.id || rIdx"
                class="timeline-record-item"
              >
                <span class="timeline-record-dot" />
                <div class="timeline-record-body">
                  <div class="timeline-record-row">
                    <span class="timeline-record-type">{{ item.rewardType === 1 ? '辅材申请' : '收到打赏' }}</span>
                    <span class="timeline-record-amount">￥{{ formatMoney(item.rewardPrice) }}</span>
                    <span class="timeline-record-time">{{ formatDateTime(item.createTime) }}</span>
                  </div>
                  <div v-if="item.describe" class="timeline-record-desc">说明：{{ item.describe }}</div>
                  <div v-if="pictureUrls(item).length" class="timeline-record-pics">
                    <a
                      v-for="(pic, pidx) in pictureUrls(item)"
                      :key="pidx"
                      :href="pic"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="timeline-record-pic-link"
                    >
                      <img :src="pic" class="timeline-record-pic-thumb" alt="凭证" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <a-empty v-else description="该师傅暂无辅材/打赏记录" :image="emptyImage" class="reward-empty" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getOrderRewards, getOrderMasters } from '@/api/order/order'

export default {
  name: 'MaterialReward',
  props: {
    orderId: { type: [Number, String], default: null }
  },
  data () {
    return {
      hasData: false,
      loading: false,
      masterList: [],
      recordList: [],
      expandedMasterIds: [],
      emptyImage: undefined
    }
  },
  computed: {
    groupedByMaster () {
      const list = []
      const recordMap = {}
      this.recordList.forEach(r => {
        const mid = r.masterOrderId
        if (!recordMap[mid]) recordMap[mid] = []
        recordMap[mid].push(r)
      })
      this.masterList.forEach(m => {
        const records = recordMap[m.id] || []
        list.push({ master: m, records })
      })
      return list
    }
  },
  watch: {
    orderId: {
      immediate: true,
      handler (id) {
        if (id) this.fetchAll()
        else {
          this.masterList = []
          this.recordList = []
          this.expandedMasterIds = []
          this.hasData = false
        }
      }
    }
  },
  methods: {
    isExpanded (masterId) {
      return this.expandedMasterIds.indexOf(masterId) !== -1
    },
    toggleExpand (masterId) {
      const idx = this.expandedMasterIds.indexOf(masterId)
      if (idx === -1) {
        this.expandedMasterIds = [...this.expandedMasterIds, masterId]
      } else {
        this.expandedMasterIds = this.expandedMasterIds.filter(id => id !== masterId)
      }
    },
    groupRecordsByDate (records) {
      if (!records || records.length === 0) return []
      const map = {}
      const order = []
      const sorted = [...records].sort((a, b) => {
        const t1 = a.createTime || ''
        const t2 = b.createTime || ''
        return String(t1).localeCompare(String(t2))
      })
      sorted.forEach(item => {
        const dateStr = this.formatDateOnly(item.createTime)
        if (!map[dateStr]) {
          map[dateStr] = []
          order.push(dateStr)
        }
        map[dateStr].push(item)
      })
      return order.map(date => ({ date, records: map[date] }))
    },
    formatDateOnly (val) {
      if (!val) return '-'
      const d = new Date(val)
      if (isNaN(d.getTime())) return '-'
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    async fetchAll () {
      if (!this.orderId) return
      this.loading = true
      this.hasData = false
      try {
        const [mastersRes, recordsRes] = await Promise.all([
          getOrderMasters(this.orderId),
          getOrderRewards(this.orderId)
        ])
        const masterList = (mastersRes && mastersRes.data) ? mastersRes.data : []
        const recordList = (recordsRes && recordsRes.data) ? recordsRes.data : []
        this.masterList = Array.isArray(masterList) ? masterList : []
        this.recordList = Array.isArray(recordList) ? recordList : []
        this.expandedMasterIds = []
        this.hasData = this.masterList.length > 0 || this.recordList.length > 0
      } catch (e) {
        this.masterList = []
        this.recordList = []
        this.expandedMasterIds = []
        this.hasData = false
      } finally {
        this.loading = false
      }
    },
    formatMoney (value) {
      const amount = Number(value)
      return Number.isNaN(amount) ? '0.00' : amount.toFixed(2)
    },
    pictureUrls (item) {
      if (!item || item.pictures == null || item.pictures === '') {
        return []
      }
      return String(item.pictures)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    },
    formatDateTime (val) {
      if (!val) return '-'
      const d = new Date(val)
      if (isNaN(d.getTime())) return val
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const h = String(d.getHours()).padStart(2, '0')
      const min = String(d.getMinutes()).padStart(2, '0')
      const s = String(d.getSeconds()).padStart(2, '0')
      return `${y}-${m}-${day} ${h}:${min}:${s}`
    }
  }
}
</script>

<style lang="less" scoped>
.order-detail-tab {
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.order-detail-tab .tab-content {
  width: 100%;
  padding: 16px;
  align-self: flex-start;
}
.reward-master-block {
  margin-bottom: 20px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  overflow: hidden;
}
.reward-master-block:last-child {
  margin-bottom: 0;
}
.reward-master-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #fafafa;
  cursor: pointer;
  user-select: none;
}
.reward-master-header:hover {
  background: #f0f0f0;
}
.reward-master-expand {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}
.reward-master-info {
  margin-left: 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.reward-master-name {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}
.reward-master-mobile {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}
.reward-master-body {
  border-top: 1px solid #e8e8e8;
  padding: 16px;
  max-height: 320px;
  overflow-y: auto;
}
.reward-empty {
  padding: 24px;
}

/* 时间轴 */
.timeline-date-group {
  position: relative;
  padding-left: 28px;
  margin-bottom: 16px;
}
.timeline-date-group:last-child {
  margin-bottom: 0;
}
.timeline-line {
  position: absolute;
  left: 5px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e8e8e8;
  z-index: 0;
}
.timeline-date-header {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}
.timeline-date-dot {
  position: absolute;
  left: -28px;
  top: 50%;
  transform: translateY(-50%);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #1890ff;
  z-index: 1;
}
.timeline-date-text {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}
.timeline-record-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
}
.timeline-record-item:last-child {
  margin-bottom: 0;
}
.timeline-record-dot {
  position: absolute;
  left: -26px;
  top: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d9d9d9;
  z-index: 1;
}
.timeline-record-body {
  flex: 1;
  min-width: 0;
}
.timeline-record-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
}
.timeline-record-type {
  font-weight: 500;
}
.timeline-record-amount {
  color: #ff4d4f;
  font-weight: 500;
}
.timeline-record-time {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}
.timeline-record-desc {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}

.timeline-record-pics {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.timeline-record-pic-link {
  display: block;
  line-height: 0;
}

.timeline-record-pic-thumb {
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
}
</style>
